import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface ClaimedAccount {
    id: number;
    account: string;
    password: string;
    claimed_at: string;
}

export default function PotatoClaim() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [claimedCount, setClaimedCount] = useState(0);
    const [maxClaims, setMaxClaims] = useState(3);
    const [canClaim, setCanClaim] = useState(false);
    const [claimed, setClaimed] = useState<ClaimedAccount[]>([]);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const fetchStatus = () => {
        api.get('/potato-account/status')
            .then(({ data }) => {
                setClaimedCount(data.claimed_count);
                setMaxClaims(data.max_claims);
                setCanClaim(data.can_claim);
                setClaimed(data.claimed);
            })
            .catch(() => toast.error('加载失败'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchStatus();
    }, []);

    const handleClaim = () => {
        if (claiming || !canClaim) return;
        setClaiming(true);
        api.post('/potato-account/claim')
            .then(({ data }) => {
                toast.success(data.message || '领取成功');
                fetchStatus();
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || '领取失败');
            })
            .finally(() => setClaiming(false));
    };

    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id);
            toast.success('已复制');
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(() => toast.error('复制失败'));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-gray-400 text-sm">加载中...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white sticky top-0 z-50 flex items-center px-4 py-3 border-b border-gray-100">
                <button onClick={() => navigate(-1)} className="p-1 -ml-1 text-gray-700">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                </button>
                <span className="ml-2 font-semibold text-gray-900">领取tato账号</span>
            </div>

            <div className="px-4 py-5 space-y-4">
                {/* Status Card */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">领取状态</span>
                        <span className="text-xs text-gray-400">
                            已领取 {claimedCount}/{maxClaims}
                        </span>
                    </div>
                    <div className="flex gap-2 mb-4">
                        {Array.from({ length: maxClaims }).map((_, i) => (
                            <div
                                key={i}
                                className={`flex-1 h-2 rounded-full ${i < claimedCount ? 'bg-green-500' : 'bg-gray-200'}`}
                            />
                        ))}
                    </div>
                    <button
                        onClick={handleClaim}
                        disabled={!canClaim || claiming}
                        className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors ${
                            canClaim && !claiming
                                ? 'bg-[#c4161c] text-white active:bg-[#a81218]'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        {claiming ? '领取中...' : !canClaim ? '已达最大领取次数' : '立即领取'}
                    </button>
                </div>

                {/* Claimed Accounts */}
                {claimed.length > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-sm font-medium text-gray-700 px-1">已领取的账号</h3>
                        {claimed.map((item) => (
                            <div key={item.id} className="bg-white rounded-xl p-4 shadow-sm">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className="text-xs text-gray-400 block mb-0.5">账号</span>
                                            <span className="text-sm font-mono font-medium text-gray-900">{item.account}</span>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(item.account, `account-${item.id}`)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                copiedId === `account-${item.id}`
                                                    ? 'bg-green-50 text-green-600'
                                                    : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                                            }`}
                                        >
                                            {copiedId === `account-${item.id}` ? '已复制' : '复制'}
                                        </button>
                                    </div>
                                    <div className="border-t border-gray-100" />
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className="text-xs text-gray-400 block mb-0.5">密码</span>
                                            <span className="text-sm font-mono font-medium text-gray-900">{item.password}</span>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(item.password, `password-${item.id}`)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                copiedId === `password-${item.id}`
                                                    ? 'bg-green-50 text-green-600'
                                                    : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                                            }`}
                                        >
                                            {copiedId === `password-${item.id}` ? '已复制' : '复制'}
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-3 text-[11px] text-gray-400">
                                    领取时间: {new Date(item.claimed_at).toLocaleString('zh-CN')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
